import React, { useEffect, useState, useContext } from "react"
import "./addModelFavorites.scss"
import TextField from "../TextField"
import PropTypes from "prop-types"
import Button from "../Button"
import FolderSelect from "../../views/MyFavorites/components/FolderSelect"
import { Mutation } from "react-apollo"
import CREATE_FAVORITES_FILE from "../../graphql/createFavoritesFile.graphql"
import { getSelectedSlicerValues } from "./helpers"
import { ModalMessage } from "../Modal"
import { getRecentFavorites } from "../../views/MyFavorites/queries"
import AppContext from "../../shared/AppContext"

const getInitialName = async (report) => {
  try {
    let selectedSlicers = await getSelectedSlicerValues(report)

    //Removes number from beginning of slice.title ex) "3. Title" => "Title"
    const removeDigitFromTitle = (str) => str.replace(/^\d+\./, "").trim()

    const firstThreeSlicers = selectedSlicers.slice(0, 3)
    const title = firstThreeSlicers
      .map((s) => `${removeDigitFromTitle(s.title)} ${s.values}`)
      .join(", ")
    return title
  } catch (err) {
    console.log("error getting initial name:", err)
    return ""
  }
}

const AddModelFavorites = (props) => {
  const [name, setName] = useState("")
  const [folderName, setFolderName] = useState("")
  const [folderId, setFolderId] = useState("")
  const [description, setDescription] = useState("")
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const { trackEvent } = useContext(AppContext)

  useEffect(() => {
    getInitialName(props.report).then((defaultName) => {
      setName(defaultName)
    })
  }, [])

  const updateErrors = (newError) => {
    setErrors({ ...errors, ...newError })
  }

  const handleCancel = () => {
    props.close()
  }

  const handleOnChange = (e) => {
    setName(e.target.value)
    updateErrors({ name: null, submit: null })
  }

  const isValid = () => {
    if (!name || name == "") {
      updateErrors({ name: "name cannot be blank." })
      return false
    }
    return true
  }

  const addFavorite = async () => {
    if (isValid()) {
      let capturedBookmark = await props.bookmark.capture()

      let variables = {
        name: name,
        slug: window.location.pathname,
        folderId,
        description,
        favoriteType: props.type,
        metadata: {
          bookmark: capturedBookmark,
          modelDisplayName: props.modelName,
        },
      }
      try {
        let result = await props.mutate({
          variables,
          refetchQueries: [
            {
              query: getRecentFavorites,
            },
          ],
        })

        if (result.data.createFavoritesFile.response.payload == "success") {
          setSuccess(true)
          trackEvent("Added Favorite", {
            favoriteType: props.type,
            location: props.trackingLocation,
            name: name,
            metadata: {
              bookmark: capturedBookmark,
              modelDisplayName: props.modelName,
            },
          })
        } else {
          updateErrors({
            submit:
              "Error: " + result.data.createFavoritesFile.response.message,
          })
        }
      } catch (err) {
        console.log("Error while adding model to favorites:", err)
        updateErrors({
          submit:
            "Error: something went wrong with your submission. Please reach out to support for help: https://app.etr.ai/help",
        })
      }
    }
  }

  if (success) {
    return (
      <ModalMessage
        messageType={"success"}
        text="Favorite successfully added."
        closeModal={handleCancel}
      />
    )
  }

  return (
    <div className="add-to-favorites-container">
      <div className="add-to-favorites-inputs-container">
        <div className="favorites-input">
          <div className="add-to-favorites-input-label">
            <span>Model Name</span>
          </div>
          <TextField
            value={props.modelName}
            name="modelName"
            fullWidth={true}
            disabled={true}
            errors
            errorOptions={{
              outline: true,
              showMessage: true,
              message: errors.modelName,
              hasError: !!errors.modelName,
            }}
          />
        </div>
        <div className="favorites-input">
          <div className="add-to-favorites-input-label">
            <span>Name</span>
          </div>
          <TextField
            value={name}
            name="name"
            fullWidth={true}
            onChange={(e) => handleOnChange(e)}
            errors
            errorOptions={{
              outline: true,
              showMessage: true,
              message: errors.name,
              hasError: !!errors.name,
            }}
          />
        </div>
        <div className="favorites-input">
          <div className="add-to-favorites-input-label">
            <span>
              Folder Name - <i>optional</i>
            </span>
          </div>
          <FolderSelect
            folderName={folderName}
            folderId={folderId}
            setFolderName={setFolderName}
            setFolderId={setFolderId}
          />
        </div>
        <div className="favorites-input">
          <div className="add-to-favorites-input-label">
            <span>
              Description - <i>optional</i>
            </span>
          </div>
          <TextField
            value={description}
            fullWidth={true}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      {errors.submit && (
        <ModalMessage
          messageType={"saveError"}
          text={errors.submit}
          style={{ textAlign: "right" }}
        />
      )}
      <div className="add-to-favorites-buttons-container">
        <Button
          action={handleCancel}
          classes={["add-to-favorites-cancel-button"]}
          text={"Cancel"}
        />
        <Button
          action={addFavorite}
          classes={["add-to-favorites-add-button"]}
          text={"Add"}
        />
      </div>
    </div>
  )
}

const AddModelFavoritesWithData = (props) => {
  return (
    <Mutation mutation={CREATE_FAVORITES_FILE}>
      {(mutate) => {
        return <AddModelFavorites mutate={mutate} {...props} />
      }}
    </Mutation>
  )
}

AddModelFavorites.propTypes = {
  modelName: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  report: PropTypes.object.isRequired,
  bookmark: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
}

export default AddModelFavoritesWithData
